import * as React from 'react';

import { useApolloClient, useQuery } from '@apollo/client';

import { DrawerContext, ModalContext } from '@notino/react-styleguide';
import {
  WishlistForCurrentUserQuery,
  WishlistForCurrentUserQueryVariables,
} from '@notino/shared/definitions/types';

import { DrawerProvider } from '@components/BottomSheet/DrawerProvider';
import { useFeatureFlags } from '@context/featureFlags/FeatureFlagsProvider';

import { WishlistPage } from './components/WishlistPage';
import { WishlistPager } from './components/WishlistPager';
import { trackWishlistPageView } from './components/WishlistPager/tracking';
import { WISHLIST_PAGE_SIZE } from './constants';
import { useWishlistOwnerId } from './hooks/useWishlistOwnerId';
import wishlistForCurrentUser from './queries/wishlistForCurrentUser.graphql';
import { createWishlistStore, WishlistContextProvider } from './store';
import { WishlistContainer } from './styled';
import { useWishlistForLoggedUserOrOwner } from './useWishlistForLoggedUserOrOwner';

const DeprecatedWishlist = () => {
  const { productIds, owner, userId } = useWishlistForLoggedUserOrOwner();
  return (
    <DrawerProvider>
      <ModalContext.Provider>
        <WishlistContainer>
          <WishlistPager
            pageSize={WISHLIST_PAGE_SIZE}
            owner={owner}
            productIds={productIds}
            userId={userId}
          />
        </WishlistContainer>
      </ModalContext.Provider>
    </DrawerProvider>
  );
};

const NewWishlist = () => {
  const owner = useWishlistOwnerId();
  const { data } = useQuery<
    WishlistForCurrentUserQuery,
    WishlistForCurrentUserQueryVariables
  >(wishlistForCurrentUser, {
    errorPolicy: 'all',
    variables: {
      count: WISHLIST_PAGE_SIZE,
    },
  });

  if (data?.WishlistForCurrentUser) {
    return (
      <NewWishlistInitialDataLoaded
        totalProductCount={
          data.WishlistForCurrentUser.listing.totalProductCount
        }
        isSharedWishlist={!!owner}
        sortByOptions={data.WishlistForCurrentUser.listing.sortByOptions}
        productIds={data.WishlistForCurrentUser.listing.products.map(
          (x) => x.webProductId
        )}
        products={data.WishlistForCurrentUser.listing.products
          .map((x) => x.product)
          .filter(Boolean)}
        hasMore={data.WishlistForCurrentUser.listing.hasMore}
      />
    );
  }

  return null;
};

const NewWishlistInitialDataLoaded = (
  props: Parameters<typeof createWishlistStore>[0]
) => {
  const [store] = React.useState(createWishlistStore(props, useApolloClient()));

  return (
    <WishlistContextProvider value={{ store }}>
      <DrawerProvider>
        <DrawerContext.Provider>
          <ModalContext.Provider>
            <WishlistContainer>
              <WishlistPage />
            </WishlistContainer>
          </ModalContext.Provider>
        </DrawerContext.Provider>
      </DrawerProvider>
    </WishlistContextProvider>
  );
};

const WishList = () => {
  const owner = useWishlistOwnerId();
  const { pd_wishlist_filtration_and_sorting } = useFeatureFlags();

  // useLayoutEffect is used for tracking the page_view event to ensure the effect runs
  // before the children render, because their useEffect hooks might run after the render.
  React.useLayoutEffect(() => {
    trackWishlistPageView();
  }, []);

  if (owner || !pd_wishlist_filtration_and_sorting) {
    return <DeprecatedWishlist />;
  }

  return <NewWishlist />;
};

export default WishList;
